import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STEPS = [
  { n: 1, title: "Master Profile",   sub: "Parent Company details" },
  { n: 2, title: "Add First Brand",  sub: "Logo, description, categories" },
  { n: 3, title: "Linking Logic",    sub: "Parent-Child hierarchy" },
  { n: 4, title: "User Permissions", sub: "Admin, Brand Manager, Sales, Finance" },
  { n: 5, title: "Bulk SKU Upload",  sub: "CSV template & API docs" },
];

const ROLE_META = {
  admin:         { label: "Admin",         hint: "Full account access — billing, brands, team & catalogue." },
  brand_manager: { label: "Brand Manager", hint: "Manage assigned brands: listings, products, RFQs." },
  sales:         { label: "Sales",         hint: "Reply to RFQs, upload quotes, no billing access." },
  finance:       { label: "Finance",       hint: "View invoices, credits & subscription — no catalogue edit." },
};

export default function EnterpriseSetupPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get("/supplier/enterprise/onboarding");
        setData(data);
        setStep(Math.min(Math.max((data.step_completed || 0) + 1, 1), 5));
      } catch (e) {
        toast.error(e.response?.data?.detail || "Enterprise tier required — this guide is available to Enterprise subscribers only.");
        navigate("/dashboard");
      }
    })();
  }, [user, loading, navigate]);

  const markStep = async (n, extra = {}) => {
    setBusy(true);
    try {
      const { data: d } = await api.put("/supplier/enterprise/onboarding", { step_completed: n, ...extra });
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save progress");
      throw e;
    } finally { setBusy(false); }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.put("/supplier/enterprise/onboarding", { step_completed: 5, status: "complete" });
      toast.success("Enterprise onboarding complete — welcome aboard.");
      navigate("/dashboard");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't finalize");
    } finally { setBusy(false); }
  };

  if (loading || !data) {
    return <div className="max-w-5xl mx-auto py-24 px-6 text-center text-[#7D8C85]">Loading your setup guide…</div>;
  }

  const done = (n) => (data.step_completed || 0) >= n;
  const active = (n) => step === n;

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="enterprise-setup-page">
      <div className="max-w-5xl mx-auto py-12 px-6">
        <div className="mb-8">
          <div className="tracking-overline text-[#B35C4A]">Enterprise · White-glove onboarding</div>
          <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] mt-1">Let's get you set up.</h1>
          <p className="text-[#4A5550] mt-3 max-w-2xl">Five short steps to make your parent-brand structure, team and catalogue live on Accommodation Suppliers. Progress saves as you go — you can always come back.</p>
        </div>

        <ProgressBar steps={STEPS} step={step} setStep={setStep} completed={data.step_completed || 0} />

        <div className="mt-8 bg-white border border-[#E8ECE9] p-6 md:p-10">
          {active(1) && <Step1MasterProfile data={data} onSave={markStep} busy={busy} onNext={() => setStep(2)} />}
          {active(2) && <Step2FirstBrand onNext={async () => { await markStep(2); setStep(3); }} busy={busy} />}
          {active(3) && <Step3LinkingLogic onNext={async () => { await markStep(3); setStep(4); }} busy={busy} />}
          {active(4) && <Step4TeamPermissions onNext={async () => { await markStep(4); setStep(5); }} busy={busy} />}
          {active(5) && <Step5BulkSku onFinish={finish} busy={busy} />}
        </div>

        <div className="mt-6 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="text-sm text-[#7D8C85] hover:text-[#11211A] disabled:opacity-40"
            data-testid="setup-prev-btn"
          >← Previous</button>
          <div className="text-xs text-[#7D8C85]">Step {step} of {STEPS.length} · {data.step_completed}/{STEPS.length} completed</div>
          <Link to="/dashboard" className="text-sm text-[#7D8C85] hover:text-[#11211A]" data-testid="setup-exit-link">Save & exit →</Link>
        </div>
      </div>
    </div>
  );
}

function ProgressBar({ steps, step, setStep, completed }) {
  return (
    <div className="flex items-center gap-2" data-testid="setup-progress">
      {steps.map((s, i) => {
        const isDone = completed >= s.n;
        const isActive = step === s.n;
        return (
          <React.Fragment key={s.n}>
            <button
              type="button"
              onClick={() => setStep(s.n)}
              className={`flex-1 min-w-0 text-left px-3 py-3 border transition-colors ${
                isActive ? "border-[#264839] bg-[#264839] text-white"
                        : isDone ? "border-[#264839]/40 bg-white text-[#264839]"
                        : "border-[#E8ECE9] bg-white text-[#7D8C85]"
              }`}
              data-testid={`setup-step-tab-${s.n}`}
            >
              <div className="text-[10px] tracking-widest uppercase opacity-80">Step {s.n}</div>
              <div className="text-sm font-medium truncate">{s.title}</div>
            </button>
            {i < steps.length - 1 && <div className="hidden md:block h-px w-4 bg-[#C4CFC9]" />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* Step 1 — Master Profile */
function Step1MasterProfile({ data, onSave, busy, onNext }) {
  const [form, setForm] = useState({
    company_name: data.parent_company?.company_name || "",
    hq_country:   data.parent_company?.hq_country   || "",
    hq_state:     data.parent_company?.hq_state     || "",
    hq_city:      data.parent_company?.hq_city      || "",
    website:      data.parent_company?.website      || "",
    primary_contact_name:  data.parent_company?.primary_contact_name  || "",
    primary_contact_email: data.parent_company?.primary_contact_email || "",
    primary_contact_phone: data.parent_company?.primary_contact_phone || "",
    tagline:      data.parent_company?.tagline      || "",
    notes:        data.parent_company?.notes        || "",
  });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) { toast.error("Company name is required."); return; }
    try {
      await onSave(1, { master_profile: form });
      toast.success("Master profile saved.");
      onNext();
    } catch { /* toast handled */ }
  };
  return (
    <form onSubmit={submit} className="space-y-5" data-testid="step1-form">
      <StepHeader n={1} title="Master Profile" sub="Tell us about your parent company. This becomes the umbrella above every brand you list." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Parent company name *"          value={form.company_name}           onChange={set("company_name")}          testid="ent-company-name" />
        <Field label="Tagline (optional)"              value={form.tagline}                onChange={set("tagline")}                testid="ent-tagline" />
        <Field label="HQ country"                      value={form.hq_country}             onChange={set("hq_country")}             testid="ent-hq-country" />
        <Field label="HQ state / region"               value={form.hq_state}               onChange={set("hq_state")}               testid="ent-hq-state" />
        <Field label="HQ city"                         value={form.hq_city}                onChange={set("hq_city")}                testid="ent-hq-city" />
        <Field label="Corporate website"               value={form.website}                onChange={set("website")}                testid="ent-website"    placeholder="https://parentcompany.com" />
        <Field label="Primary contact name"            value={form.primary_contact_name}   onChange={set("primary_contact_name")}   testid="ent-contact-name" />
        <Field label="Primary contact email"           value={form.primary_contact_email}  onChange={set("primary_contact_email")}  testid="ent-contact-email" type="email" />
        <Field label="Primary contact phone"           value={form.primary_contact_phone}  onChange={set("primary_contact_phone")}  testid="ent-contact-phone" />
      </div>
      <Field label="Anything the account manager should know?" value={form.notes} onChange={set("notes")} testid="ent-notes" as="textarea" rows={3} />
      <div className="flex justify-end">
        <button type="submit" disabled={busy} className="btn-primary" data-testid="step1-save-next">{busy ? "Saving…" : "Save & continue →"}</button>
      </div>
    </form>
  );
}

/* Step 2 — Add First Brand */
function Step2FirstBrand({ onNext, busy }) {
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", categories: "", logo_url: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const refresh = async () => {
    try { const { data } = await api.get("/brand-profiles"); setBrands(data || []); } catch { setBrands([]); }
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Brand name is required."); return; }
    setSaving(true);
    try {
      await api.post("/brand-profiles", {
        name: form.name,
        description: form.description,
        logo_url: form.logo_url,
        categories: form.categories.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Brand created.");
      setForm({ name: "", description: "", categories: "", logo_url: "" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create brand");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="step2-form">
      <StepHeader n={2} title="Add your first brand" sub="Each brand under your Enterprise account gets its own logo, description and category set. Add one now — you can add more from your Dashboard." />
      <form onSubmit={create} className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b border-[#F0EFEA] pb-6">
        <Field label="Brand name *"                    value={form.name}         onChange={set("name")}         testid="brand-name" />
        <Field label="Logo URL (upload from Dashboard)" value={form.logo_url}     onChange={set("logo_url")}     testid="brand-logo-url" placeholder="https://…" />
        <Field label="Categories (comma-separated slugs)" value={form.categories} onChange={set("categories")}   testid="brand-categories" placeholder="linen,room-amenities" />
        <Field label="Short description"                value={form.description}  onChange={set("description")}  testid="brand-description" as="textarea" rows={3} className="md:col-span-2" />
        <div className="md:col-span-2 flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="brand-create-btn">{saving ? "Creating…" : "Add brand"}</button>
        </div>
      </form>
      <div>
        <div className="tracking-overline mb-2">Brands on this account</div>
        {brands.length === 0 ? (
          <div className="text-sm text-[#7D8C85] italic">No brands yet — add your first above.</div>
        ) : (
          <ul className="space-y-2">
            {brands.map((b) => (
              <li key={b.id} className="flex items-center justify-between border border-[#E8ECE9] px-4 py-3" data-testid={`brand-row-${b.id}`}>
                <div>
                  <div className="font-medium text-[#11211A]">{b.name}</div>
                  <div className="text-xs text-[#7D8C85]">{(b.categories || []).join(" · ") || "No categories yet"}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#264839]">brand id · {b.id.slice(0, 8)}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={onNext} disabled={busy || brands.length === 0} className="btn-primary" data-testid="step2-next">{busy ? "Saving…" : "Continue →"}</button>
      </div>
    </div>
  );
}

/* Step 3 — Linking Logic — Visual diagram */
function Step3LinkingLogic({ onNext, busy }) {
  return (
    <div className="space-y-5" data-testid="step3-diagram">
      <StepHeader n={3} title="Linking logic" sub="Here's how listings, products and RFQs flow up your parent-child hierarchy." />
      <div className="bg-[#F0EFEA] p-8 md:p-12" data-testid="hierarchy-diagram">
        <div className="max-w-2xl mx-auto">
          <div className="mx-auto w-max px-6 py-3 bg-[#264839] text-white text-center">
            <div className="text-[10px] tracking-widest uppercase opacity-80">Parent company</div>
            <div className="font-serif text-lg">Your Enterprise Account</div>
          </div>
          <div className="mx-auto w-px h-8 bg-[#264839]/40"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="relative">
                <div className="absolute -top-8 left-1/2 w-px h-8 bg-[#264839]/40"></div>
                <div className="border border-[#264839]/40 bg-white p-4 text-center">
                  <div className="text-[10px] tracking-widest uppercase text-[#B35C4A]">Brand {i}</div>
                  <div className="font-serif text-base text-[#11211A] mt-1">Logo · Categories · Listings</div>
                </div>
                <div className="mx-auto w-px h-6 bg-[#264839]/20"></div>
                <div className="text-xs text-center text-[#4A5550] leading-relaxed">
                  <div>Listings</div>
                  <div>Products</div>
                  <div>RFQs</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <ul className="text-sm text-[#4A5550] leading-relaxed space-y-2 pl-5 list-disc">
        <li>Every listing you create is attached to exactly one brand, but shares the parent company's billing and analytics.</li>
        <li>RFQs sent to a brand's listing route to that brand's Sales role and to the parent Admin.</li>
        <li>Bulk uploads (Step 5) can populate multiple brands at once via a <code>brand_slug</code> column.</li>
        <li>Category-Leader placement is granted per parent company — every brand you add inherits the rotation slot.</li>
      </ul>
      <div className="flex justify-end">
        <button type="button" onClick={onNext} disabled={busy} className="btn-primary" data-testid="step3-next">{busy ? "Saving…" : "Got it — continue →"}</button>
      </div>
    </div>
  );
}

/* Step 4 — User Permissions */
function Step4TeamPermissions({ onNext, busy }) {
  const [team, setTeam] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "brand_manager" });
  const [saving, setSaving] = useState(false);
  const refresh = async () => {
    try { const { data } = await api.get("/supplier/enterprise/team"); setTeam(data || []); } catch { setTeam([]); }
  };
  useEffect(() => { refresh(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const invite = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error("Email required"); return; }
    setSaving(true);
    try {
      await api.post("/supplier/enterprise/team", form);
      toast.success(`${form.email} invited as ${ROLE_META[form.role].label}.`);
      setForm({ email: "", name: "", role: "brand_manager" });
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't invite");
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!confirm("Remove this teammate?")) return;
    try { await api.delete(`/supplier/enterprise/team/${id}`); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-5" data-testid="step4-form">
      <StepHeader n={4} title="User permissions" sub="Invite teammates and assign a role. Everyone signs in with their own credentials." />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {Object.entries(ROLE_META).map(([k, v]) => (
          <div key={k} className="border border-[#E8ECE9] p-3" data-testid={`role-card-${k}`}>
            <div className="tracking-overline text-[#264839]">{v.label}</div>
            <div className="text-xs text-[#4A5550] mt-1 leading-relaxed">{v.hint}</div>
          </div>
        ))}
      </div>

      <form onSubmit={invite} className="grid grid-cols-1 md:grid-cols-4 gap-3 border-t border-[#F0EFEA] pt-6">
        <Field label="Teammate email *"      value={form.email} onChange={set("email")} testid="team-email" type="email" />
        <Field label="Full name (optional)" value={form.name}  onChange={set("name")}  testid="team-name" />
        <div>
          <label className="block text-xs font-medium text-[#4A5550] mb-1">Role</label>
          <select value={form.role} onChange={set("role")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="team-role">
            {Object.entries(ROLE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button type="submit" disabled={saving} className="btn-primary w-full justify-center" data-testid="team-invite-btn">{saving ? "Sending…" : "Invite"}</button>
        </div>
      </form>

      <div>
        <div className="tracking-overline mb-2">Invited</div>
        {team.length === 0 ? (
          <div className="text-sm text-[#7D8C85] italic">No teammates yet.</div>
        ) : (
          <ul className="space-y-2">
            {team.map((t) => (
              <li key={t.id} className="flex items-center justify-between border border-[#E8ECE9] px-4 py-3" data-testid={`team-row-${t.id}`}>
                <div>
                  <div className="font-medium text-[#11211A]">{t.name || t.email}</div>
                  <div className="text-xs text-[#7D8C85]">{t.email} · <span className="text-[#264839]">{ROLE_META[t.role]?.label || t.role}</span> · {t.status}</div>
                </div>
                <button onClick={() => remove(t.id)} className="text-xs text-[#B35C4A] hover:underline" data-testid={`team-remove-${t.id}`}>Remove</button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={onNext} disabled={busy} className="btn-primary" data-testid="step4-next">{busy ? "Saving…" : "Continue →"}</button>
      </div>
    </div>
  );
}

/* Step 5 — Bulk SKU Upload */
function Step5BulkSku({ onFinish, busy }) {
  const templateUrl = "/csv/enterprise-sku-template.csv";
  const apiDocsUrl = "/dashboard#api-docs";
  return (
    <div className="space-y-5" data-testid="step5-form">
      <StepHeader n={5} title="Bulk SKU upload" sub="Load your entire catalogue in one shot — CSV template for one-time uploads, API for continuous sync." />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-[#E8ECE9] p-5" data-testid="csv-card">
          <div className="tracking-overline text-[#B35C4A] mb-2">CSV upload (recommended)</div>
          <div className="font-serif text-xl text-[#11211A] mb-2">One-shot catalogue import</div>
          <p className="text-sm text-[#4A5550] mb-4">Download the template, fill in your SKUs (title, brand_slug, category, price, MOQ, image URL) and upload from your Dashboard → Products → Bulk Import.</p>
          <a href={templateUrl} download className="btn-primary text-xs" data-testid="csv-template-link">Download CSV template</a>
        </div>
        <div className="border border-[#E8ECE9] p-5" data-testid="api-card">
          <div className="tracking-overline text-[#B35C4A] mb-2">API sync</div>
          <div className="font-serif text-xl text-[#11211A] mb-2">Continuous integration</div>
          <p className="text-sm text-[#4A5550] mb-4">Use <code>POST /api/products</code> with your Enterprise API key to keep pricing and stock in sync with your PIM.</p>
          <Link to={apiDocsUrl} className="btn-primary text-xs" data-testid="api-docs-link">Open API docs</Link>
        </div>
      </div>
      <div className="text-xs text-[#7D8C85] italic">Not ready? Skip and finish — you can bulk-load any time from the Dashboard.</div>
      <div className="flex justify-end">
        <button type="button" onClick={onFinish} disabled={busy} className="btn-primary" data-testid="step5-finish">{busy ? "Finishing…" : "Finish setup ✓"}</button>
      </div>
    </div>
  );
}

/* --- shared bits --- */
function StepHeader({ n, title, sub }) {
  return (
    <div>
      <div className="tracking-overline text-[#B35C4A]">Step {n} of 5</div>
      <h2 className="font-serif text-3xl text-[#11211A] mt-1">{title}</h2>
      <p className="text-sm text-[#4A5550] mt-2 max-w-2xl">{sub}</p>
    </div>
  );
}

function Field({ label, value, onChange, testid, as = "input", rows = 2, type = "text", placeholder = "", className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-[#4A5550] mb-1">{label}</label>
      {as === "textarea" ? (
        <textarea rows={rows} value={value} onChange={onChange} placeholder={placeholder} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid={testid} />
      ) : (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid={testid} />
      )}
    </div>
  );
}
